import { GridAlpha, TextInput, DatePicker, Select, Combobox } from '@applyboard/crystal-ui';
import { COUNTRY_NAMES, CountryIsoCode } from '~/constants';
import { useGradeSchemas } from '~/data/useGradeSchemas';
import { useMediaQuery } from '~/hooks/useMediaQuery';

/**
 * Crystal UI DatePicker throws if `value` is an empty string.
 * Coerce empty strings to a safe fallback for initial render.
 */
export function safeDateValue(value: string): string {
  return value || '';
}

export function getEducationDateBounds() {
  const now = new Date();
  const minDate = new Date(now.getFullYear() - 50, 0, 1).toISOString();
  const maxDate = new Date(now.getFullYear() + 15, 0, 1).toISOString();
  return { minDate, maxDate };
}

const { minDate: EDUCATION_MIN_DATE, maxDate: EDUCATION_MAX_DATE } = getEducationDateBounds();

/** Codes must match the backend EducationLevel codes */
export const EDUCATION_LEVEL_OPTIONS = [
  { value: 'PRIMARY_SCHOOL', label: 'Primary School' },
  { value: 'MIDDLE_SCHOOL', label: 'Middle School' },
  { value: 'HIGH_SCHOOL', label: 'High School' },
  { value: 'UNDERGRAD', label: 'Undergraduate' },
  { value: 'GRADUATE', label: 'Graduate' },
  { value: 'PHD', label: 'PhD' },
] as const;

export const COUNTRY_OPTIONS = Object.entries(COUNTRY_NAMES)
  .map(([code, name]) => ({ value: code, label: name }))
  .sort((a, b) => a.label.localeCompare(b.label));

export type EducationHistoryEditData = {
  schoolName: string;
  educationLevel: string;
  educationCountry: string;
  credentialReceived: string;
  fieldOfStudy: string;
  degree: string;
  gradeSchemeId: string;
  gradeSchemeName: string;
  startDate: string;
  endDate: string;
};

/** Maps frontend field names to the API metadata field names */
const FIELD_TO_API_KEY: Record<keyof EducationHistoryEditData, string> = {
  schoolName: 'schoolName',
  educationLevel: 'educationLevel',
  educationCountry: 'issuingCountry',
  credentialReceived: 'credentialReceived',
  fieldOfStudy: 'fieldOfStudy',
  degree: 'degree',
  gradeSchemeId: 'gradeSchemeId',
  gradeSchemeName: 'gradeSchemeName',
  startDate: 'startDate',
  endDate: 'endDate',
};

/**
 * Optional fields that can be explicitly cleared by sending empty strings.
 */
const OPTIONAL_FIELDS = new Set<string>(['degree', 'fieldOfStudy', 'gradeSchemeId', 'gradeSchemeName']);

/**
 * Compares edited data against the original snapshot and returns only the
 * fields that actually changed, mapped to the API metadata key names.
 *
 * For optional fields (fieldOfStudy, gradeSchemeId, gradeSchemeName):
 *   - Empty strings are sent as empty strings to explicitly clear the field
 *
 * For required fields:
 *   - Empty strings are sent as undefined (treated as missing/invalid)
 *
 * Special handling: When gradeSchemeId is cleared, gradeSchemeName is
 * automatically cleared as well to maintain data consistency.
 */
export function getChangedMetadata(
  original: EducationHistoryEditData,
  edited: EducationHistoryEditData
): Record<string, string | undefined> | null {
  const changed: Record<string, string | undefined> = {};
  let hasChanges = false;

  for (const field of Object.keys(FIELD_TO_API_KEY) as Array<keyof EducationHistoryEditData>) {
    if (edited[field] !== original[field]) {
      const key = FIELD_TO_API_KEY[field];
      const value = edited[field];

      // For optional fields, send empty string when cleared to explicitly clear the field
      if (OPTIONAL_FIELDS.has(key)) {
        changed[key] = value || '';
      } else {
        // For required fields, send undefined when empty (validation will catch this)
        changed[key] = value || undefined;
      }

      hasChanges = true;
    }
  }

  // When the final gradeSchemeId is empty, ensure gradeSchemeName is also cleared
  // to prevent stale grade scheme name from persisting. This handles cases where:
  if (edited.gradeSchemeId === '' && edited.gradeSchemeName !== '') {
    changed.gradeSchemeName = '';
    hasChanges = true;
  }

  return hasChanges ? changed : null;
}

export type GradeSchemeConfig = Readonly<{
  enabled: boolean;
  required: boolean;
}>;

type EducationHistoryEditFieldsProps = Readonly<{
  editedData: EducationHistoryEditData;
  onChange: (field: keyof EducationHistoryEditData, value: string) => void;
  gradeScheme?: GradeSchemeConfig;
}>;

const GRADE_SCHEME_DEFAULTS: GradeSchemeConfig = { enabled: false, required: false };

/**
 * Shared form fields component for inline editing of education history metadata.
 * Used by both the Submitted tab and Evaluation tab in edit mode.
 */
export function EducationHistoryEditFields({
  editedData,
  onChange,
  gradeScheme = GRADE_SCHEME_DEFAULTS,
}: EducationHistoryEditFieldsProps) {
  const { gradeSchemas } = useGradeSchemas();
  const { isMobile } = useMediaQuery();

  const gradeSchemeOptions = gradeSchemas.map((schema) => ({
    value: schema.id,
    label: schema.name,
  }));

  const columns = isMobile ? '1fr' : 'repeat(3, 1fr)';
  const fieldCount = gradeScheme.enabled ? 9 : 8;
  const rowCount = isMobile ? fieldCount : Math.ceil(fieldCount / 3);

  return (
    <GridAlpha rows={`repeat(${rowCount}, auto)`} columnGap={2} rowGap={6} columns={columns}>
      <GridAlpha.Item>
        <Select
          label="Education level"
          value={editedData.educationLevel}
          onChange={(value: string) => onChange('educationLevel', value)}
          appearance="styled"
          required
        >
          {EDUCATION_LEVEL_OPTIONS.map((option) => (
            <Select.Option key={option.value} value={option.value} label={option.label} />
          ))}
        </Select>
      </GridAlpha.Item>
      <GridAlpha.Item>
        <Combobox
          label="Education country"
          value={editedData.educationCountry}
          onChange={(value: string) => onChange('educationCountry', value as CountryIsoCode)}
          required
        >
          {COUNTRY_OPTIONS.map((option) => (
            <Combobox.Option key={option.value} value={option.value} label={option.label} />
          ))}
        </Combobox>
      </GridAlpha.Item>
      <GridAlpha.Item>
        <TextInput
          label="Name of institution"
          value={editedData.schoolName}
          onChange={(value: string) => onChange('schoolName', value)}
          required
        />
      </GridAlpha.Item>
      <GridAlpha.Item>
        <TextInput
          label="Credential gained (or expected)"
          value={editedData.credentialReceived}
          onChange={(value: string) => onChange('credentialReceived', value)}
          required
        />
      </GridAlpha.Item>
      <GridAlpha.Item>
        <TextInput label="Degree" value={editedData.degree} onChange={(value: string) => onChange('degree', value)} />
      </GridAlpha.Item>
      <GridAlpha.Item>
        <TextInput
          label="Field of study"
          value={editedData.fieldOfStudy}
          onChange={(value: string) => onChange('fieldOfStudy', value)}
        />
      </GridAlpha.Item>
      <GridAlpha.Item>
        <DatePicker
          label="Education start"
          value={safeDateValue(editedData.startDate)}
          onChange={(value: string) => onChange('startDate', value)}
          granularity="month"
          minDate={EDUCATION_MIN_DATE}
          maxDate={EDUCATION_MAX_DATE}
          required
        />
      </GridAlpha.Item>
      <GridAlpha.Item>
        <DatePicker
          label="Education end (or expected)"
          value={safeDateValue(editedData.endDate)}
          onChange={(value: string) => onChange('endDate', value)}
          granularity="month"
          minDate={EDUCATION_MIN_DATE}
          maxDate={EDUCATION_MAX_DATE}
          required
        />
      </GridAlpha.Item>
      {gradeScheme.enabled && (
        <GridAlpha.Item>
          <Select
            label="Grade scheme"
            value={editedData.gradeSchemeId}
            onChange={(value: string) => {
              onChange('gradeSchemeId', value);
              const selected = gradeSchemeOptions.find((opt) => opt.value === value);
              if (selected) {
                onChange('gradeSchemeName', selected.label);
              } else {
                onChange('gradeSchemeName', '');
              }
            }}
            appearance="styled"
            required={gradeScheme.required}
          >
            {gradeSchemeOptions.map((option) => (
              <Select.Option key={option.value} value={option.value} label={option.label} />
            ))}
          </Select>
        </GridAlpha.Item>
      )}
    </GridAlpha>
  );
}
